import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Package, Wrench, ClipboardList, Calendar, Users,
  ShoppingCart, ArrowRight, CheckCircle, ChevronRight,
  FileText, BarChart2,
} from 'lucide-react';

function Step({ n, text }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-600 text-white text-sm font-bold flex items-center justify-center mt-0.5">
        {n}
      </span>
      <p className="text-gray-700">{text}</p>
    </div>
  );
}

function SectionHeading({ icon: Icon, id, title, subtitle }) {
  return (
    <div id={id} className="flex items-start gap-4 mb-6 pt-4">
      <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon className="w-6 h-6 text-primary-600" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}

const CmmsHelpPage = () => {
  const { t } = useTranslation();

  const toc = [
    { id: 'getting-started', label: t('help.cmms.toc_start') },
    { id: 'assets',          label: t('help.cmms.toc_assets') },
    { id: 'work-orders',     label: t('help.cmms.toc_wo') },
    { id: 'pm-schedules',    label: t('help.cmms.toc_pm') },
    { id: 'inventory',       label: t('help.cmms.toc_inv') },
    { id: 'team',            label: t('help.cmms.toc_team') },
    { id: 'reports',         label: t('help.cmms.toc_reports') },
  ];

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Link to="/portal/help" className="hover:text-primary-600">{t('help.helpCenter')}</Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-medium">{t('help.cmms.title')}</span>
      </div>

      <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-2xl p-8 border border-primary-100 mb-2">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-primary-600 rounded-full mb-4">
          <Package className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('help.cmms.title')}</h1>
        <p className="text-gray-600 max-w-2xl">{t('help.cmms.subtitle')}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sticky ToC */}
        <aside className="lg:w-52 flex-shrink-0">
          <div className="sticky top-6 bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">{t('help.onThisPage')}</p>
            <ul className="space-y-2">
              {toc.map(item => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className="text-sm text-gray-600 hover:text-primary-600 flex items-center gap-1.5"
                  >
                    <ArrowRight className="w-3.5 h-3.5 flex-shrink-0" />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 max-w-3xl space-y-16 bg-white rounded-2xl border border-gray-200 p-8">

          {/* ── 1. Getting Started ─────────────────────────────────────── */}
          <section>
            <SectionHeading icon={Package} id="getting-started"
              title={t('help.cmms.s1_title')} subtitle={t('help.cmms.s1_subtitle')} />
            <div className="space-y-1 mb-5">
              <Step n={1} text={t('help.cmms.s1_step1')} />
              <Step n={2} text={t('help.cmms.s1_step2')} />
              <Step n={3} text={t('help.cmms.s1_step3')} />
              <Step n={4} text={t('help.cmms.s1_step4')} />
            </div>
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('help.cmms.s1_keyConcepts')}</p>
              <div className="space-y-2 text-sm text-gray-600">
                {[
                  [t('help.cmms.s1_branch'), t('help.cmms.s1_branchDesc')],
                  [t('help.cmms.s1_asset'),  t('help.cmms.s1_assetDesc')],
                  [t('help.cmms.s1_wo'),     t('help.cmms.s1_woDesc')],
                  [t('help.cmms.s1_pm'),     t('help.cmms.s1_pmDesc')],
                  [t('help.cmms.s1_inv'),    t('help.cmms.s1_invDesc')],
                ].map(([k, v]) => (
                  <div key={k} className="flex gap-3">
                    <span className="font-semibold w-32 flex-shrink-0 text-gray-800">{k}</span>
                    <span>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── 2. Assets ──────────────────────────────────────────────── */}
          <section>
            <SectionHeading icon={Wrench} id="assets"
              title={t('help.cmms.s2_title')} subtitle={t('help.cmms.s2_subtitle')} />
            <div className="space-y-1 mb-5">
              <Step n={1} text={t('help.cmms.s2_step1')} />
              <Step n={2} text={t('help.cmms.s2_step2')} />
              <Step n={3} text={t('help.cmms.s2_step3')} />
              <Step n={4} text={t('help.cmms.s2_step4')} />
              <Step n={5} text={t('help.cmms.s2_step5')} />
            </div>
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-5 mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('help.cmms.s2_keyFields')}</p>
              <table className="w-full text-xs">
                <tbody className="divide-y divide-gray-100 text-gray-700">
                  {[
                    [t('help.cmms.s2_fName'),        t('help.cmms.s2_fNameDesc')],
                    [t('help.cmms.s2_fCode'),         t('help.cmms.s2_fCodeDesc')],
                    [t('help.cmms.s2_fCategory'),     t('help.cmms.s2_fCategoryDesc')],
                    [t('help.cmms.s2_fBranch'),       t('help.cmms.s2_fBranchDesc')],
                    [t('help.cmms.s2_fLocation'),     t('help.cmms.s2_fLocationDesc')],
                    [t('help.cmms.s2_fSerial'),       t('help.cmms.s2_fSerialDesc')],
                    [t('help.cmms.s2_fNextService'),  t('help.cmms.s2_fNextServiceDesc')],
                  ].map(([f, d]) => (
                    <tr key={f}>
                      <td className="py-1.5 pr-3 font-mono text-primary-700 w-36">{f}</td>
                      <td className="py-1.5 text-gray-600">{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-start gap-2 p-4 bg-blue-50 rounded-xl border border-blue-200 text-sm text-blue-800">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
              <span>{t('help.cmms.s2_note')}</span>
            </div>
          </section>

          {/* ── 3. Work Orders ─────────────────────────────────────────── */}
          <section>
            <SectionHeading icon={ClipboardList} id="work-orders"
              title={t('help.cmms.s3_title')} subtitle={t('help.cmms.s3_subtitle')} />
            <div className="space-y-1 mb-5">
              <Step n={1} text={t('help.cmms.s3_step1')} />
              <Step n={2} text={t('help.cmms.s3_step2')} />
              <Step n={3} text={t('help.cmms.s3_step3')} />
              <Step n={4} text={t('help.cmms.s3_step4')} />
              <Step n={5} text={t('help.cmms.s3_step5')} />
              <Step n={6} text={t('help.cmms.s3_step6')} />
            </div>
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('help.cmms.s3_statuses')}</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[
                  { statusKey: 's3_open',       color: 'bg-blue-100 text-blue-800',     descKey: 's3_openDesc' },
                  { statusKey: 's3_inProgress',  color: 'bg-yellow-100 text-yellow-800', descKey: 's3_inProgressDesc' },
                  { statusKey: 's3_onHold',      color: 'bg-orange-100 text-orange-800', descKey: 's3_onHoldDesc' },
                  { statusKey: 's3_completed',   color: 'bg-green-100 text-green-800',   descKey: 's3_completedDesc' },
                ].map(({ statusKey, color, descKey }) => (
                  <div key={statusKey} className="flex items-start gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${color}`}>{t(`help.cmms.${statusKey}`)}</span>
                    <span className="text-gray-600 text-xs">{t(`help.cmms.${descKey}`)}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── 4. PM Schedules ────────────────────────────────────────── */}
          <section>
            <SectionHeading icon={Calendar} id="pm-schedules"
              title={t('help.cmms.s4_title')} subtitle={t('help.cmms.s4_subtitle')} />
            <div className="space-y-1 mb-5">
              <Step n={1} text={t('help.cmms.s4_step1')} />
              <Step n={2} text={t('help.cmms.s4_step2')} />
              <Step n={3} text={t('help.cmms.s4_step3')} />
              <Step n={4} text={t('help.cmms.s4_step4')} />
              <Step n={5} text={t('help.cmms.s4_step5')} />
            </div>
            <div className="flex items-start gap-2 p-4 bg-green-50 rounded-xl border border-green-200 text-sm text-green-800">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" />
              <span>{t('help.cmms.s4_note')}</span>
            </div>
          </section>

          {/* ── 5. Inventory & Warehouses ──────────────────────────────── */}
          <section>
            <SectionHeading icon={ShoppingCart} id="inventory"
              title={t('help.cmms.s5_title')} subtitle={t('help.cmms.s5_subtitle')} />
            <div className="space-y-1 mb-5">
              <Step n={1} text={t('help.cmms.s5_step1')} />
              <Step n={2} text={t('help.cmms.s5_step2')} />
              <Step n={3} text={t('help.cmms.s5_step3')} />
              <Step n={4} text={t('help.cmms.s5_step4')} />
            </div>
            <div className="flex items-start gap-2 p-4 bg-amber-50 rounded-xl border border-amber-200 text-sm text-amber-800">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
              <span>{t('help.cmms.s5_note')}</span>
            </div>
          </section>

          {/* ── 6. Team & Permissions ──────────────────────────────────── */}
          <section>
            <SectionHeading icon={Users} id="team"
              title={t('help.cmms.s6_title')} subtitle={t('help.cmms.s6_subtitle')} />
            <div className="space-y-1 mb-5">
              <Step n={1} text={t('help.cmms.s6_step1')} />
              <Step n={2} text={t('help.cmms.s6_step2')} />
              <Step n={3} text={t('help.cmms.s6_step3')} />
              <Step n={4} text={t('help.cmms.s6_step4')} />
            </div>
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('help.cmms.s6_roles')}</p>
              <table className="w-full text-xs">
                <tbody className="divide-y divide-gray-100 text-gray-700">
                  {[
                    [t('help.cmms.s6_admin'),      t('help.cmms.s6_adminDesc')],
                    [t('help.cmms.s6_supervisor'),  t('help.cmms.s6_supervisorDesc')],
                    [t('help.cmms.s6_technician'),  t('help.cmms.s6_technicianDesc')],
                    [t('help.cmms.s6_viewer'),      t('help.cmms.s6_viewerDesc')],
                  ].map(([r, a]) => (
                    <tr key={r}>
                      <td className="py-1.5 pr-3 font-mono text-primary-700 w-28">{r}</td>
                      <td className="py-1.5 text-gray-600">{a}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── 7. Reports & Analytics ─────────────────────────────────── */}
          <section>
            <SectionHeading icon={BarChart2} id="reports"
              title={t('help.cmms.s7_title')} subtitle={t('help.cmms.s7_subtitle')} />
            <p className="text-gray-600 mb-5">{t('help.cmms.s7_intro')}</p>
            <div className="space-y-4">
              {[
                { titleKey: 's7_r1', descKey: 's7_r1Desc' },
                { titleKey: 's7_r2', descKey: 's7_r2Desc' },
                { titleKey: 's7_r3', descKey: 's7_r3Desc' },
                { titleKey: 's7_r4', descKey: 's7_r4Desc' },
                { titleKey: 's7_r5', descKey: 's7_r5Desc' },
              ].map(r => (
                <div key={r.titleKey} className="rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-primary-600" />
                    <h3 className="font-semibold text-gray-900 text-sm">{t(`help.cmms.${r.titleKey}`)}</h3>
                  </div>
                  <p className="text-sm text-gray-600">{t(`help.cmms.${r.descKey}`)}</p>
                </div>
              ))}
            </div>
          </section>

        </main>
      </div>

      <div className="flex gap-4">
        <Link to="/portal/help" className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
          {t('help.backToHelp')}
        </Link>
        <Link to="/portal/support" className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium">
          {t('help.openSupport')}
        </Link>
      </div>
    </div>
  );
};

export default CmmsHelpPage;
